import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Slider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { sendAction } from '@/services/sensors.service'
import { futureCommands, getEquipments } from '@/services/realtime.service'
import { makeid } from '@/utils/global.utils'
import { insertAction } from '@/utils/realtime.utils'
import { ParsedObject } from '../types'
import {
    getSharedSetpointRange,
    getZoneCommandCapabilityFromEquipments,
    GetEquipmentsResponse,
} from './SelectedZonesTelecommand.utils'

const TIME_OPTIONS: string[] = []
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 10) {
        TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    }
}

const marks = [
    {
        value: 20,
        label: '20°C',
    },
    {
        value: 25,
        label: '25°C',
    },
]

const formatValue = (value: number) => `${value}°C`

interface SelectedZonesTelecommandProps {
    open: boolean
    onClose: () => void
    onSubmitted: () => void
    selectedObjects: ParsedObject[]
    site: {
        hash: string
    }
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)
type DriveValue = 'on' | 'off'

const SelectedZonesTelecommand: FC<SelectedZonesTelecommandProps> = ({
    open,
    onClose,
    onSubmitted,
    selectedObjects,
    site,
}) => {
    const { t } = useTranslation('site', { keyPrefix: 'realtime.multiZoneTelecommand' })
    const [drive, setDrive] = useState<DriveValue>('on')
    const [setTemp, setSetTemp] = useState(20)
    const [sending, setSending] = useState(false)
    const [submitError, setSubmitError] = useState(false)
    const [isScheduleMode, setIsScheduleMode] = useState(false)
    const [scheduleDate, setScheduleDate] = useState('')
    const [scheduleTime, setScheduleTime] = useState('')

    const selectedObjectIds = useMemo(
        () => selectedObjects.map((object) => object.objectId),
        [selectedObjects]
    )
    const selectedObjectsById = useMemo(
        () => new Map(selectedObjects.map((object) => [object.objectId, object])),
        [selectedObjects]
    )
    const sortedSelectedObjectIds = useMemo(
        () => [...selectedObjectIds].sort((a, b) => a - b),
        [selectedObjectIds]
    )
    const selectionKey = useMemo(() => sortedSelectedObjectIds.join(','), [sortedSelectedObjectIds])

    const capabilitiesQuery = useQuery({
        queryKey: ['selected-zones-telecommand', site?.hash, selectionKey],
        enabled: open && Boolean(site?.hash) && selectedObjectIds.length > 0,
        queryFn: async () => {
            return Promise.all(
                selectedObjectIds.map(async (objectId) => {
                    const response = (await getEquipments({
                        hash: site.hash,
                        objectId,
                    })) as GetEquipmentsResponse

                    return getZoneCommandCapabilityFromEquipments(objectId, response?.equipments)
                })
            )
        },
    })

    const capabilities = capabilitiesQuery.data || []
    const supportedObjectCount = capabilities.filter(
        (capability) => capability.driveActuatorId || capability.setTempActuatorId
    ).length
    const driveSupportedCount = capabilities.filter((capability) => capability.driveActuatorId).length
    const setTempSupportedCount = capabilities.filter((capability) => capability.setTempActuatorId).length
    const sharedSetpointRange = getSharedSetpointRange(capabilities)
    const supportsDrive = driveSupportedCount > 0
    const supportsSetpoint = Boolean(sharedSetpointRange) && setTempSupportedCount > 0
    const unsupportedObjectCount = Math.max(0, selectedObjects.length - supportedObjectCount)

    useEffect(() => {
        if (!open) {
            return
        }

        setDrive('on')
        setSubmitError(false)
        setIsScheduleMode(false)
        setScheduleDate('')
        setScheduleTime('')
    }, [open, selectionKey])

    useEffect(() => {
        if (!sharedSetpointRange) {
            return
        }

        setSetTemp((current) => clamp(current, sharedSetpointRange.min, sharedSetpointRange.max))
    }, [sharedSetpointRange])

    const submit = async () => {
        setSubmitError(false)
        setSending(true)
        const driveValue = drive === 'on' ? 1 : 0

        try {
            for (const capability of capabilities) {
                const object = selectedObjectsById.get(capability.objectId)
                if (!object) {
                    continue
                }

                const modifications = []
                if (supportsDrive && capability.driveActuatorId) {
                    modifications.push({
                        id: capability.driveActuatorId,
                        value: driveValue,
                        title: '',
                        actuatorId: capability.driveActuatorId,
                        createdAt: new Date(),
                        hash: makeid(),
                        objectName: object.name,
                        objectId: object.objectId,
                        SetTemp: null,
                        Drive: driveValue,
                    })
                }

                if (supportsSetpoint && capability.setTempActuatorId) {
                    modifications.push({
                        id: capability.setTempActuatorId,
                        value: setTemp,
                        title: '',
                        actuatorId: capability.setTempActuatorId,
                        createdAt: new Date(),
                        hash: makeid(),
                        objectName: object.name,
                        objectId: object.objectId,
                        SetTemp: setTemp,
                        Drive: null,
                    })
                }

                if (modifications.length === 0) {
                    continue
                }

                const shouldSend = insertAction(modifications)
                if (!shouldSend) {
                    continue
                }

                await sendAction({
                    groupHashes: [],
                    modifications,
                    hash: site.hash,
                    fromDashboard: true,
                    selectedObject: { Name: object.name },
                })
            }

            onSubmitted()
            onClose()
        } catch (error) {
            setSubmitError(true)
        } finally {
            setSending(false)
        }
    }

    const enterScheduleMode = () => {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        setScheduleDate(tomorrow.toISOString().split('T')[0])

        const now = new Date()
        let hours = now.getHours()
        let minutes = Math.round(now.getMinutes() / 10) * 10
        if (minutes === 60) {
            minutes = 0
            hours = (hours + 1) % 24
        }
        setScheduleTime(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`)
        setIsScheduleMode(true)
    }

    const submitScheduled = async () => {
        setSending(true)
        setSubmitError(false)
        const driveValue = drive === 'on' ? 1 : 0

        try {
            const commands = capabilities.flatMap((capability) => {
                const cmds: Array<{
                    ObjectId: number
                    Time: string
                    Date: string
                    State: number | null
                    Setpoint: number | null
                }> = []
                if (supportsDrive && capability.driveActuatorId) {
                    cmds.push({
                        ObjectId: capability.objectId,
                        Time: scheduleTime,
                        Date: scheduleDate,
                        State: driveValue,
                        Setpoint: null,
                    })
                }
                if (supportsSetpoint && capability.setTempActuatorId) {
                    cmds.push({
                        ObjectId: capability.objectId,
                        Time: scheduleTime,
                        Date: scheduleDate,
                        State: null,
                        Setpoint: setTemp,
                    })
                }
                return cmds
            })

            await futureCommands({ hash: site.hash, commands })
            onSubmitted()
            onClose()
        } catch {
            setSubmitError(true)
        } finally {
            setSending(false)
        }
    }

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('title', { count: selectedObjects.length })}</DialogTitle>
            <DialogContent>
                {capabilitiesQuery.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        <Typography color="text.secondary">
                            {t('description', { count: selectedObjects.length })}
                        </Typography>

                        {supportsDrive && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography>{t('fields.state')}</Typography>
                                <ToggleButtonGroup
                                    value={drive}
                                    exclusive
                                    onChange={(_, newValue: DriveValue | null) => {
                                        if (newValue !== null) {
                                            setDrive(newValue)
                                        }
                                    }}
                                    color="primary"
                                >
                                    <ToggleButton value="on">ON</ToggleButton>
                                    <ToggleButton value="off">OFF</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}

                        {supportsSetpoint && sharedSetpointRange && (
                            <Box>
                                <Typography sx={{ mt: 1 }}>{t('fields.setpointTemperature')}</Typography>
                                <Box sx={{ px: 2 }}>
                                    <Slider
                                        aria-label={t('fields.setpointTemperature')}
                                        onChange={(_, newValue: number | number[]) =>
                                            setSetTemp(Array.isArray(newValue) ? newValue[0] : newValue)
                                        }
                                        getAriaValueText={formatValue}
                                        step={1}
                                        value={setTemp}
                                        valueLabelDisplay="auto"
                                        marks={[
                                            { value: sharedSetpointRange.min, label: `${sharedSetpointRange.min}°C` },
                                            ...marks.filter(
                                                (mark) =>
                                                    mark.value > sharedSetpointRange.min &&
                                                    mark.value < sharedSetpointRange.max
                                            ),
                                            { value: sharedSetpointRange.max, label: `${sharedSetpointRange.max}°C` },
                                        ]}
                                        min={sharedSetpointRange.min}
                                        max={sharedSetpointRange.max}
                                    />
                                </Box>
                            </Box>
                        )}

                        {!supportsSetpoint && setTempSupportedCount > 0 && (
                            <Alert severity="warning">{t('warnings.noSharedSetpoint')}</Alert>
                        )}

                        {unsupportedObjectCount > 0 && (
                            <Alert severity="info">
                                {t('warnings.unsupportedObjects', { count: unsupportedObjectCount })}
                            </Alert>
                        )}

                        {supportsDrive && driveSupportedCount < selectedObjects.length && (
                            <Alert severity="info">{t('warnings.partialState', { count: driveSupportedCount })}</Alert>
                        )}

                        {supportsSetpoint && setTempSupportedCount < selectedObjects.length && (
                            <Alert severity="info">
                                {t('warnings.partialSetpoint', { count: setTempSupportedCount })}
                            </Alert>
                        )}

                        {!supportsDrive && !supportsSetpoint && (
                            <Alert severity="warning">{t('warnings.noSupportedZones')}</Alert>
                        )}

                        <Alert severity="warning">{t('warning')}</Alert>

                        {isScheduleMode && (
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <TextField
                                    label={t('schedule.date')}
                                    type="date"
                                    value={scheduleDate}
                                    onChange={(e) => setScheduleDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1, minWidth: 160 }}
                                />
                                <TextField
                                    select
                                    label={t('schedule.time')}
                                    value={scheduleTime}
                                    onChange={(e) => setScheduleTime(e.target.value)}
                                    sx={{ flex: 1, minWidth: 140 }}
                                >
                                    {TIME_OPTIONS.map((time) => (
                                        <MenuItem key={time} value={time}>
                                            {time}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}

                        {submitError && <Alert severity="error">{t('errors.submit')}</Alert>}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} color="inherit" disabled={sending}>
                    {t('actions.cancel')}
                </Button>
                {isScheduleMode ? (
                    <>
                        <Button color="inherit" onClick={() => setIsScheduleMode(false)} disabled={sending}>
                            {t('actions.sendNow')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={submitScheduled}
                            disabled={
                                sending ||
                                capabilitiesQuery.isLoading ||
                                capabilitiesQuery.isError ||
                                (!supportsDrive && !supportsSetpoint) ||
                                !scheduleDate ||
                                !scheduleTime
                            }
                            startIcon={sending ? <CircularProgress color="inherit" size={14} /> : undefined}
                        >
                            {t('actions.confirmSchedule')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={enterScheduleMode}
                            disabled={
                                sending ||
                                capabilitiesQuery.isLoading ||
                                capabilitiesQuery.isError ||
                                (!supportsDrive && !supportsSetpoint)
                            }
                        >
                            {t('actions.scheduleFuture')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={submit}
                            disabled={
                                sending ||
                                capabilitiesQuery.isLoading ||
                                capabilitiesQuery.isError ||
                                (!supportsDrive && !supportsSetpoint)
                            }
                            startIcon={sending ? <CircularProgress color="inherit" size={14} /> : undefined}
                        >
                            {t('actions.save')}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default SelectedZonesTelecommand
